import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

const DEFAULT_CURRENCY_CODE = 'USD';
const DEFAULT_LOCALE = 'en-us';
export type DisplayType = 'currency' | 'percentage' | 'number' | 'string' | 'translate' | 'fraction';

export interface DisplayValueDescriptor {
  value: number | string;
  displayType: DisplayType;
  outOfValue?: number | string;
  currencyCode?: string;
  locale?: string;
  maxDecimalPlaces?: number;
}

@Pipe({
  name: 'displayValue',
})
export class GalaxyDisplayValuePipe implements PipeTransform, OnDestroy {
  value: number | string = '';
  outOfValue?: number | string;
  displayType?: DisplayType;
  currencyCode: string = DEFAULT_CURRENCY_CODE;
  locale = DEFAULT_LOCALE;
  displayValue: string | null = null;

  onTranslationChange?: Subscription;
  onLangChange?: Subscription;
  onDefaultLangChange?: Subscription;
  digitsInfo?: string;

  constructor(private translateService: TranslateService) {}

  updateValue(): void {
    switch (this.displayType) {
      case 'currency': {
        const currencyPipe = new CurrencyPipe(this.locale);
        this.displayValue = currencyPipe.transform(this.value, this.currencyCode, 'symbol', '1.0-2', this.locale);
        break;
      }
      case 'percentage': {
        const percentPipe = new PercentPipe(this.locale);
        this.displayValue = percentPipe.transform(this.value, this.digitsInfo ? this.digitsInfo : '1.0-2', this.locale);
        break;
      }
      case 'number': {
        const decimalPipe = new DecimalPipe(this.locale);
        this.displayValue = decimalPipe.transform(this.value, this.digitsInfo, this.locale);
        break;
      }
      case 'string':
        this.displayValue = this.value as string;
        break;
      case 'translate':
        this.displayValue = this.translateService.instant(this.value as string);
        break;
      case 'fraction':
        this.displayValue = this.value + '/' + this.outOfValue;
        break;
    }
  }

  transform(displayValue: DisplayValueDescriptor): string | null {
    if (!displayValue) {
      return null;
    }
    const { value, outOfValue, displayType, currencyCode = DEFAULT_CURRENCY_CODE, locale } = displayValue;
    this.value = value;
    this.outOfValue = outOfValue;
    this.displayType = displayType;
    this.currencyCode = currencyCode;
    this.locale = locale || DEFAULT_LOCALE;
    this.digitsInfo =
      displayValue.maxDecimalPlaces || displayValue.maxDecimalPlaces === 0
        ? '1.0-' + displayValue.maxDecimalPlaces
        : undefined;

    this.updateValue();

    if (!this.onTranslationChange) {
      this.onTranslationChange = this.translateService.onTranslationChange.subscribe(() => {
        this.updateValue();
      });
    }
    if (!this.onLangChange) {
      this.onLangChange = this.translateService.onLangChange.subscribe(() => {
        this.updateValue();
      });
    }
    if (!this.onDefaultLangChange) {
      this.onDefaultLangChange = this.translateService.onDefaultLangChange.subscribe(() => {
        this.updateValue();
      });
    }

    return this.displayValue;
  }

  ngOnDestroy(): void {
    this.onTranslationChange?.unsubscribe();
    this.onTranslationChange = undefined;
    this.onLangChange?.unsubscribe();
    this.onLangChange = undefined;
    this.onDefaultLangChange?.unsubscribe();
    this.onDefaultLangChange = undefined;
  }
}
